import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  SearchableMultiSelect,
  SearchableSelect,
  type SelectOption,
} from "@/components/ui/searchable-select";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useDepartments,
  useInviteUser,
  useUpdateUser,
  type UserInput,
} from "@/hooks/useUserMutations";
import { useUsers } from "@/hooks/useUsers";
import type { Role, UserRow } from "@/lib/types";

const ALL_ROLES: { value: Role; label: string }[] = [
  { value: "user",     label: "User" },
  { value: "director", label: "Director" },
  { value: "ceo",      label: "CEO" },
  { value: "admin",    label: "Admin" },
];

/** Returns the role options the current actor is allowed to assign.
 *  Mirrors private.utils.can_assign_role (rank strictly below own;
 *  Admin can mint peer Admins). */
function allowedRolesFor(role?: Role): { value: Role; label: string }[] {
  if (role === "admin")    return ALL_ROLES;
  if (role === "ceo")      return ALL_ROLES.filter((r) => r.value === "user" || r.value === "director");
  if (role === "director") return ALL_ROLES.filter((r) => r.value === "user");
  return [];
}

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass a row for edit mode, omit for invite. */
  user?: UserRow | null;
}

/**
 * Invite (create) + edit form. Role dropdown is filtered to what
 * the current actor is allowed to assign; if they try to overreach
 * the backend clamps silently.
 *
 * Department picker shows for CEO+ on both invite and edit; Director
 * sees their own department as a fixed read-only label (backend
 * inherits actor's dept anyway).
 */
export function UserFormDialog({ open, onOpenChange, user }: UserFormDialogProps) {
  const isEdit = !!user;
  const { data: actor } = useCurrentUser();
  const departments = useDepartments();
  const invite = useInviteUser();
  const update = useUpdateUser();
  const mutation = isEdit ? update : invite;

  const allowedRoles = useMemo(() => allowedRolesFor(actor?.role), [actor?.role]);

  const [email,        setEmail]        = useState("");
  const [firstName,    setFirstName]    = useState("");
  const [lastName,     setLastName]     = useState("");
  const [role,         setRole]         = useState<Role>("user");
  const [position,     setPosition]     = useState("");
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [managerId,    setManagerId]    = useState<string | null>(null);
  const [ledDeptIds,   setLedDeptIds]   = useState<string[]>([]);

  // Show the Director picker only when a CEO is creating a regular user.
  // Backend enforces the same rule — picker is just a friendlier UX.
  const needsManagerPicker = !isEdit && actor?.role === "ceo" && role === "user";
  // Directors in the CEO's department (server scopes via get_subordinate_users).
  const directors = useUsers({ role: "director" });

  // Multi-dept picker (CEO target). Show whenever the form is editing
  // or creating a CEO — backend enforces actor permission, so we just
  // surface the controls; if the actor lacks rights the API returns 403.
  const showLedDeptsPicker = role === "ceo" && !!actor?.is_ceo_level;

  useEffect(() => {
    if (!open) return;
    if (user) {
      setEmail(user.email);
      setFirstName(user.first_name);
      setLastName(user.last_name);
      setRole(user.role);
      setPosition(user.position || "");
      setDepartmentId(user.department_id);
      setLedDeptIds(user.led_department_ids ?? []);
    } else {
      setEmail(""); setFirstName(""); setLastName("");
      // CEOs almost always invite Directors (regular users need to be
      // assigned to a Director anyway, which is extra friction), so
      // default the role picker to "director" for CEO actors and to
      // the first allowed option for everyone else.
      const defaultRole: Role = actor?.role === "ceo"
        ? "director"
        : (allowedRoles[0]?.value || "user");
      setRole(defaultRole);
      setPosition("");
      setDepartmentId(actor?.is_admin_level ? null : actor?.department_id ?? null);
      setLedDeptIds([]);
    }
    setManagerId(null);
    invite.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  const canSubmit =
    (isEdit || email.trim().length > 0)
    && firstName.trim().length > 0
    && (!needsManagerPicker || managerId != null)   // block client-side if missing
    && !mutation.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const input: UserInput = {
      first_name: firstName.trim(),
      last_name:  lastName.trim(),
      role,
      position:   position.trim() || undefined,
    };
    if (!isEdit) input.email = email.trim().toLowerCase();
    if (actor?.is_ceo_level) input.department_id = departmentId;
    if (needsManagerPicker)  input.manager_id    = managerId;
    if (showLedDeptsPicker)  input.led_department_ids = ledDeptIds;

    if (isEdit && user) {
      update.mutate({ id: user.id, input }, { onSuccess: () => onOpenChange(false) });
    } else {
      invite.mutate(input, { onSuccess: () => onOpenChange(false) });
    }
  }

  const err = mutation.error as
    | { response?: { data?: Record<string, unknown> | { detail?: string } } }
    | undefined;
  const errorMsg = formatApiError(err);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit “${user?.full_name || user?.email}”` : "Invite a new user"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update fields below. Email can't change here — that's a separate verified flow."
              : "We'll send a one-time set-password link to their email."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          {!isEdit && (
            <div className="space-y-1.5">
              <Label htmlFor="uf-email">Email <span className="text-destructive">*</span></Label>
              <Input
                id="uf-email" type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="name@mopd.gov.et"
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="uf-first">First name <span className="text-destructive">*</span></Label>
              <Input
                id="uf-first" required maxLength={100}
                value={firstName} onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="uf-last">Last name</Label>
              <Input
                id="uf-last" maxLength={100}
                value={lastName} onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="uf-role">Role</Label>
              <select
                id="uf-role"
                value={role} onChange={(e) => setRole(e.target.value as Role)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
              >
                {allowedRoles.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Only roles below yours are listed.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="uf-position">Position</Label>
              <Input
                id="uf-position" maxLength={120}
                value={position} onChange={(e) => setPosition(e.target.value)}
                placeholder="e.g. Climate Finance Analyst"
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="uf-dept">Department</Label>
              {actor?.is_ceo_level ? (() => {
                // Admin sees the full org list. A plain CEO sees only
                // the departments they actually lead — not every dept
                // in the org.
                const ledSet = new Set(actor.led_department_ids ?? []);
                const scoped = actor.is_admin_level
                  ? (departments.data ?? [])
                  : (departments.data ?? []).filter((d) => ledSet.has(d.id));
                const opts: SelectOption<string>[] = scoped.map((d) => ({
                  value: d.id,
                  label: d.name,
                }));
                return (
                  <>
                    <SearchableSelect<string>
                      id="uf-dept"
                      value={departmentId}
                      onChange={setDepartmentId}
                      options={opts}
                      placeholder="— None —"
                      emptyHint={
                        actor.is_admin_level
                          ? "No departments yet."
                          : "You don't lead any departments."
                      }
                      disabled={departments.isLoading}
                    />
                    {!actor.is_admin_level && (
                      <p className="text-xs text-muted-foreground">
                        Only the {scoped.length} department{scoped.length === 1 ? "" : "s"} you lead {scoped.length === 1 ? "is" : "are"} listed.
                      </p>
                    )}
                    {!actor.is_admin_level && scoped.length === 0 && (
                      <p className="text-xs text-destructive">
                        You don't lead any departments yet — ask an Admin to assign you to one.
                      </p>
                    )}
                  </>
                );
              })() : (
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  {actor?.department_name || "— You don't belong to a department —"}
                </div>
              )}
              {!actor?.is_ceo_level && (
                <p className="text-xs text-muted-foreground">
                  Director-created users inherit your department; only CEO/Admin can move people between departments.
                </p>
              )}
            </div>

            {showLedDeptsPicker && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Departments this CEO leads</Label>
                <SearchableMultiSelect<string>
                  values={ledDeptIds}
                  onChange={setLedDeptIds}
                  options={(departments.data ?? []).map((d) => {
                    const takenByOther = d.ceo_id != null && d.ceo_id !== user?.id;
                    return {
                      value: d.id,
                      label: d.name,
                      hint: takenByOther
                        ? `Currently led by ${d.ceo_name} — picking will reject.`
                        : undefined,
                    };
                  })}
                  emptyHint={departments.isLoading ? "Loading…" : "No departments yet."}
                />
                <p className="text-xs text-muted-foreground">
                  Pick every department this CEO is responsible for. One dept can have only one CEO at a time.
                </p>
              </div>
            )}

            {needsManagerPicker && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="uf-manager">
                  Assign to Director <span className="text-destructive">*</span>
                </Label>
                <SearchableSelect<string>
                  id="uf-manager"
                  value={managerId}
                  onChange={setManagerId}
                  options={(directors.data?.results ?? []).map((d) => ({
                    value: d.id,
                    label: d.full_name || d.email,
                    hint: d.department_name || undefined,
                  }))}
                  placeholder="— Pick a Director —"
                  emptyHint="No Directors found."
                  clearable={false}
                  disabled={directors.isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Regular users report up through a Director. The new user lands on the
                  Director's roster, not yours.
                </p>
                {directors.data && directors.data.results.length === 0 && (
                  <p className="text-xs text-destructive">
                    No Directors in your department yet — create one first.
                  </p>
                )}
              </div>
            )}
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button" variant="ghost"
              onClick={() => onOpenChange(false)} disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isEdit ? "Saving…" : "Sending invite…"}</>
                : isEdit
                  ? <><Save className="mr-2 h-4 w-4" />Save changes</>
                  : <><UserPlus className="mr-2 h-4 w-4" />Send invite</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatApiError(
  err: { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined,
): string | null {
  if (!err?.response?.data) return null;
  const data = err.response.data as Record<string, unknown> & { detail?: string };
  if (typeof data.detail === "string") return data.detail;
  for (const [field, msgs] of Object.entries(data)) {
    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
      return `${field}: ${msgs[0]}`;
    }
  }
  return "Save failed.";
}
